import { useTitle } from '@flippingbook/web.react-hooks/dist/useTitle';
import { Button } from '@flippingbook/web.react-kit/dist/Button';
import { L10n } from '@flippingbook/web.react-kit/dist/L10n';
import { Link as UILink } from '@flippingbook/web.react-kit/dist/Link';
import { Paper } from '@flippingbook/web.react-kit/dist/Paper';
import { useEffect, useMemo } from 'react';

import { useLoginPage } from '@/hooks/useLoginPage';
import { loc } from '@/localization';
import { analyticsService } from '@/services/instance/analytics';
import { discoveryService } from '@/services/instance/discovery';
import { optionsService } from '@/services/instance/optionsService';
import { Field } from '@/site/components/Field';
import { Link } from '@/site/components/Link';
import { Separator } from '@/site/components/Separator';
import { ErrorBoxes } from '@/site/pages/Login/ErrorBoxes';
import { ExternalLoginButtons } from '@/site/pages/Login/ExternalLoginButtons';

import { b, e } from './style.css';

const siteUrl = discoveryService.get('site');

function resetPolicyError() {
    optionsService.resetExternalLoginFailureReason();
}

const onEmailInputStart = () => analyticsService.sendInputStartEvent('email');
const onPasswordInputStart = () => analyticsService.sendInputStartEvent('password');

const onSubmitClick = () => analyticsService.sendLoginButtonClick();

const onLogin = () => analyticsService.sendLoginEvent('login_form');
const onLoginFailed = () => analyticsService.sendLoginErrorEvent();

export function LoginPage() {
    const {
        restoreUrl,
        registrationUrl,
        selectSamlUrl,
        register,
        errors,
        cleanErrors,
        onFormSubmit,
        isSubmitting,
        validationRules,
    } = useLoginPage({
        onLogin,
        onLoginFailed,
    });

    errors.password = useMemo(() => {
        if (
            errors.password?.message &&
            typeof errors.password.message === 'string' &&
            errors.password.message.startsWith('SomethingWentWrong$$')
        ) {
            const [, errorCodes] = errors.password.message.split('$$');
            const href = `${siteUrl}contacts?contact-form=support&subject=login error ${errorCodes}`.replace(
                /\s/g,
                '%20',
            );
            const message = (
                <L10n
                    text={loc.Login.SomethingWentWrong}
                    replaces={[
                        ['{0}', errorCodes ? ` [${errorCodes}]` : ''],
                        [
                            '{1}',
                            <UILink
                                href={href}
                                target="_blank"
                            >
                                {loc.Login.ContactUs}
                            </UILink>,
                        ],
                    ]}
                />
            ) as unknown as string;

            return {
                ...errors.password,
                message,
            };
        }

        return errors.password;
    }, [errors.password]);

    const loginFromCanva = optionsService.getAppId() === 'canva';
    const title = loginFromCanva ? loc.Login.WelcomeBackFromCanva : loc.Login.WelcomeBack;
    const subTitle = loginFromCanva ? loc.Login.LogInToYourAccountFromCanva : loc.Login.LogInToYourAccount;

    useTitle(loc.Main.AccountPageTitle.replace('{0}', loc.Login.LoginTitle));

    const externalLoginFailureReason = optionsService.getExternalLoginFailureReason();
    const hasFacebookEmailError = externalLoginFailureReason === 'requiredInfoMissing';
    const hasPolicyError = externalLoginFailureReason === 'DeclinedByPolicy';
    const showExternalLoginButtons = !(hasFacebookEmailError || hasPolicyError);

    const emailErrorType = errors.email?.type;
    useEffect(() => {
        if (emailErrorType) {
            void analyticsService.sendEmailInvalidEvent();
        }
    }, [emailErrorType]);

    const passwordErrorType = errors.password?.type;
    useEffect(() => {
        if (passwordErrorType) {
            void analyticsService.sendPasswordInvalidEvent();
        }
    }, [passwordErrorType]);

    return (
        <div className={b.name}>
            <Paper
                className={e.card}
                radius="large"
            >
                <h1 className={e.title}>{title}</h1>
                <p className={e.subtitle}>{subTitle}</p>
                <form
                    className={e.form}
                    onSubmit={onFormSubmit}
                    noValidate
                >
                    <Field
                        name="email"
                        type="email"
                        className={e.emailField}
                        placeholder={loc.Login.WorkEmail}
                        onChange={cleanErrors}
                        register={register}
                        errors={errors}
                        readOnly={isSubmitting}
                        autoFocus
                        validationRules={validationRules}
                        onStartTyping={onEmailInputStart}
                    />
                    <Field
                        name="password"
                        type="password"
                        className={e.passwordField}
                        placeholder={loc.Login.Password}
                        onChange={cleanErrors}
                        register={register}
                        errors={errors}
                        readOnly={isSubmitting}
                        validationRules={validationRules}
                        onStartTyping={onPasswordInputStart}
                    />
                    <div className={e.restorePasswordLinkWrapper}>
                        <Link
                            className={e.restorePasswordLink}
                            to={restoreUrl}
                        >
                            {loc.Login.RestorePassword}
                        </Link>
                    </div>
                    <Button
                        className={e.submitButton}
                        type="submit"
                        theme="green"
                        size="large"
                        progress={isSubmitting}
                        disabled={isSubmitting}
                        onClick={onSubmitClick}
                    >
                        {loc.Login.DoLogin}
                    </Button>
                </form>
                <ErrorBoxes
                    hasFacebookEmailError={hasFacebookEmailError}
                    hasPolicyError={hasPolicyError}
                    resetPolicyError={resetPolicyError}
                    registrationUrl={registrationUrl}
                    selectSamlUrl={selectSamlUrl}
                />
                {showExternalLoginButtons && (
                    <>
                        <Separator />
                        <ExternalLoginButtons />
                    </>
                )}
                <div className={e.footer}>
                    <div className={e.needAnAccount}>
                        {loc.Login.NeedAnAccount}{' '}
                        <Link
                            to={registrationUrl}
                            onClick={resetPolicyError}
                        >
                            {loc.Login.DoSignUp}
                        </Link>
                    </div>
                    <div className={e.divider} />
                    <Link
                        className={e.samlLink}
                        to={selectSamlUrl}
                    >
                        {loc.Login.DoSsoLogin}
                    </Link>
                </div>
            </Paper>
        </div>
    );
}
